import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, } from "react";
import { useAuth } from "./authContext";
import { getFavoritos, addFavorito, removeFavorito } from "../services/api";
import toast from "react-hot-toast";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user, isAuth } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cargar favoritos SOLO si hay sesión activa
  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!isAuth || !user?._id) {
        setItems([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const resp = await getFavoritos();
        if (!mounted) return;
        setItems(Array.isArray(resp?.data) ? resp.data : []);
      } catch (e) {
        console.error("Error cargando favoritos", e);
        if (!mounted) return;
        setItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [isAuth, user?._id]);

  // Set con IDs favoritos
  const favoriteIds = useMemo(() => {
    return new Set(
      items
        .map((f) => String(f.medicamento_id?._id ?? f.medicamento_id))
        .filter(Boolean)
    );
  }, [items]);

  const isFavorite = useCallback(
    (medicamentoId) => {
      if (!medicamentoId) return false;
      return favoriteIds.has(String(medicamentoId));
    },
    [favoriteIds]
  );

  const markFavorite = useCallback(
    async (medicamento) => {
      if (!isAuth || !user?._id) {
        toast.error("Debes iniciar sesión para guardar favoritos");
        return;
      }

      const medId = medicamento?._id ?? medicamento?.id;
      if (!medId) {
        console.warn("No se encontró ID del medicamento", medicamento);
        return;
      }

      // Si ya existe, eliminar
      if (isFavorite(medId)) {
        try {
          await removeFavorito(medId);

          setItems((prev) =>
            prev.filter(
              (f) =>
                String(f.medicamento_id?._id ?? f.medicamento_id) !== String(medId)
            )
          );

          toast.success("Favorito eliminado");
        } catch (e) {
          console.error("No se pudo agregar favorito");
          console.error("status:", e?.response?.status);
          console.error("data:", e?.response?.data);
          console.error("error completo:", e);
          toast.error("No se pudo guardar en favoritos");
        }
        return;
      }

      // Si no existe, agregar
      try {
        const fav = await addFavorito(medId);

        setItems((prev) => {
          const existe = prev.some(
            (f) =>
              String(f.medicamento_id?._id ?? f.medicamento_id) === String(medId)
          );
          if (existe) return prev;

          return [...prev, { ...fav, medicamento_id: medicamento }];
        });

        toast.success("Favorito guardado");
      } catch (e) {
        console.error("No se pudo agregar favorito", e);
        toast.error("No se pudo guardar en favoritos");
      }
    },
    [isAuth, user?._id, isFavorite]
  );

  const reloadFavoritos = useCallback(async () => {
    if (!isAuth || !user?._id) return;

    setLoading(true);
    try {
      const resp = await getFavoritos();
      setItems(Array.isArray(resp?.data) ? resp.data : []);
    } catch (e) {
      console.error("Error recargando favoritos", e);
    } finally {
      setLoading(false);
    }
  }, [isAuth, user?._id]);

  const value = useMemo(
    () => ({
      favorites: items,
      loading,
      isFavorite,
      markFavorite,
      reloadFavoritos,
    }),
    [items, loading, isFavorite, markFavorite, reloadFavoritos]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    throw new Error("useFavorites debe usarse dentro de <FavoritesProvider>");
  }
  return ctx;
}